import { ERROR_CODES, type ApiError, type ErrorCode, type ValidationDetails } from "@salesboard/contracts";

/**
 * The one error type the UI handles. Carries the machine `code` to branch on and the
 * `requestId` so the error state can show a reference support can grep for.
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  readonly details: unknown;
  readonly requestId: string;

  constructor(envelope: ApiError) {
    super(envelope.message);
    this.name = "ApiClientError";
    this.status = envelope.status;
    this.code = envelope.code;
    this.details = envelope.details;
    this.requestId = envelope.requestId;
  }

  get isValidation(): boolean {
    return this.code === ERROR_CODES.VALIDATION_FAILED;
  }

  get isStaleVersion(): boolean {
    return this.code === ERROR_CODES.STALE_VERSION;
  }

  /** Field errors when the failure was validation, otherwise nothing. */
  get fieldErrors(): Record<string, string[]> {
    if (!this.isValidation) return {};
    const details = this.details as ValidationDetails | undefined;
    return details?.fieldErrors ?? {};
  }

  /** The server's current state when a write was refused as stale. */
  currentState<T>(): T | undefined {
    if (!this.isStaleVersion) return undefined;
    return (this.details as { current?: T } | undefined)?.current;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & { body?: unknown };

/**
 * Same-origin fetch wrapper. The API is always at `/api`, so there is no base URL to
 * configure and nothing environment-specific baked into the bundle.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;

  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...rest,
      credentials: "same-origin",
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    // The request never reached the API — it is down, the network dropped, or a proxy
    // refused. Surfaced as an ApiClientError so the screen can say *that*, rather than
    // showing a generic failure with no reference; "unknown" is honest here, because no
    // request id was ever assigned.
    throw new ApiClientError({
      status: 0,
      code: ERROR_CODES.UNAVAILABLE,
      message: "Cannot reach the server. Check your connection, then try again.",
      requestId: "no-response",
    });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();

  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    // Not JSON: something in front of the API answered — a dev-server proxy error page, a
    // gateway, an HTML error. Treat it as unavailable rather than crashing on a parse.
    throw new ApiClientError({
      status: res.status,
      code: ERROR_CODES.UNAVAILABLE,
      message: "The server returned an unexpected response.",
      requestId: res.headers.get("X-Request-Id") ?? "no-response",
    });
  }

  if (!res.ok) {
    throw new ApiClientError(toEnvelope(parsed, res));
  }

  return parsed as T;
}

/**
 * Every API failure should already be the standard envelope. A response that is not
 * one means something in front of the API answered — a proxy, a gateway, a crash
 * before the filter ran — so synthesise an envelope rather than throwing a shapeless
 * error the UI cannot render.
 */
function toEnvelope(parsed: unknown, res: Response): ApiError {
  const candidate = parsed as Partial<ApiError> | undefined;
  if (candidate && typeof candidate.code === "string" && typeof candidate.message === "string") {
    return {
      status: candidate.status ?? res.status,
      code: candidate.code as ErrorCode,
      message: candidate.message,
      details: candidate.details,
      requestId: candidate.requestId ?? res.headers.get("X-Request-Id") ?? "unknown",
    };
  }

  return {
    status: res.status,
    code: res.status === 401 ? ERROR_CODES.UNAUTHENTICATED : ERROR_CODES.INTERNAL,
    message: res.status === 401 ? "Your session has expired." : "The server could not be reached.",
    requestId: res.headers.get("X-Request-Id") ?? "unknown",
  };
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: "GET" }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  /** For a write that replaces a whole collection — the stage order arrives as one list. */
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

/**
 * Build a query string from the parameter vocabulary, omitting empties.
 *
 * A multi-value filter arrives as an array and travels **comma-joined** (`owner=id1,id2`),
 * which is the API contract's own spelling and what the server's comma-split schemas expect.
 * Repeated `owner=` pairs would be a second encoding of the same idea, and an empty array is
 * omitted for the same reason an empty string is: it filters nothing.
 */
export function toQueryString(params: Record<string, string | number | (string | number)[] | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    if (Array.isArray(value)) {
      if (value.length > 0) search.set(key, value.join(","));
      continue;
    }
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}
